/**
 * AI アシスタントのホーム（用途のボタン）
 *
 * 並びとおすすめはサーバーがイベントの状態からルールで決める（GET /api/chat/home）。
 * 重要度の高い用途ほど少ないタップで届くように、深さ 1 をここに並べ、
 * 深さ 2・3 は［すべて見る］から開く。
 */

import { ChatBubbleLeftRightIcon, ChevronRightIcon, ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import AiMark, { AiMarkLegend } from '@/components/chat/AiMark';
import { CHAT_HOME_SECONDARY_FLOWS } from '@/constants/chat';
import type { ChatFlowButton, ChatHomeResponse, ChatHomeShortcut } from '@/services/chat';

interface ChatHomeProps {
  home: ChatHomeResponse;
  hasPrevious: boolean;
  onOpenPrevious: () => void;
  onStart: (flowId: string, slots: Record<string, string>) => void;
  onOpenAll: () => void;
  onOpenHistory: () => void;
  onClearContext: () => void;
}

// 2 列の並びで数が奇数なら、最後の 1 つを横いっぱいにして空きを作らない
function isLoneLast(index: number, count: number): boolean {
  return count % 2 === 1 && index === count - 1;
}

function ShortcutButton({
  label,
  hint,
  aiFree,
  ai,
  primary,
  wide,
  onClick,
}: {
  label: string;
  hint?: string | null;
  aiFree?: boolean;
  /** AI を使う用途（印を付ける） */
  ai?: boolean;
  primary?: boolean;
  wide?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-[3.25rem] w-full flex-col items-start justify-center rounded-xl border px-3 py-2 text-left transition-colors hover:bg-primary-50 ${
        primary ? 'border-primary-300' : 'border-text-200'
      } ${wide ? 'col-span-2' : ''}`}
    >
      <span className="text-sm font-medium text-text-900">
        {label}
        {ai && (
          <span className="ml-1">
            <AiMark />
          </span>
        )}
      </span>
      {(hint || aiFree) && (
        <span className="text-[11px] text-text-500">{hint ?? 'AIを使いません'}</span>
      )}
    </button>
  );
}

export default function ChatHome({
  home,
  hasPrevious,
  onOpenPrevious,
  onStart,
  onOpenAll,
  onOpenHistory,
  onClearContext,
}: ChatHomeProps) {
  const flows = new Map((home.flows ?? []).map((flow) => [flow.flowId, flow]));
  const context = home.contextEvent ?? null;
  const shortcuts = home.shortcuts ?? [];
  const shortcutIds = new Set(shortcuts.map((s) => s.flowId));

  // ヘッダーから開いたときは、イベントを選ぶ深さ 1 の用途も並べる
  const generalFlows: ChatFlowButton[] = context
    ? []
    : (home.flows ?? []).filter(
        (flow) =>
          flow.depth === 1 &&
          flow.available &&
          !CHAT_HOME_SECONDARY_FLOWS.includes(flow.flowId as (typeof CHAT_HOME_SECONDARY_FLOWS)[number]) &&
          !shortcuts.some((s) => s.flowId === flow.flowId && !s.eventId),
      );
  const secondary = CHAT_HOME_SECONDARY_FLOWS.map((id) => flows.get(id)).filter(
    (flow): flow is ChatFlowButton => !!flow && flow.available && !shortcutIds.has(flow.flowId),
  );

  const start = (shortcut: ChatHomeShortcut) =>
    onStart(shortcut.flowId, shortcut.eventId ? { eventId: shortcut.eventId } : {});
  const startFlow = (flow: ChatFlowButton) => onStart(flow.flowId, {});

  return (
    <div className="space-y-4">
      {context && (
        <div className="flex">
          <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-primary-50 py-1 pl-3 pr-1 text-xs font-medium text-primary-700">
            <span className="truncate">
              {context.title}（{context.dateLabel}）
            </span>
            <button
              type="button"
              onClick={onClearContext}
              className="rounded-full p-0.5 hover:bg-primary-100"
              aria-label="このイベントを外す"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>
      )}

      {shortcuts.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-[11px] font-medium tracking-wide text-text-500">
            {context ? 'このイベントで' : 'いま動いているイベント'}
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {shortcuts.map((shortcut, index) => (
              <ShortcutButton
                key={`${shortcut.flowId}-${shortcut.eventId ?? ''}`}
                label={shortcut.label}
                hint={shortcut.hint}
                aiFree={flows.get(shortcut.flowId)?.usesModel === false}
                ai={flows.get(shortcut.flowId)?.usesModel === true}
                primary
                wide={isLoneLast(index, shortcuts.length)}
                onClick={() => start(shortcut)}
              />
            ))}
          </div>
        </section>
      )}

      {home.recommended && (
        <section className="space-y-2">
          <h3 className="text-[11px] font-medium tracking-wide text-text-500">いまおすすめ</h3>
          <ShortcutButton
            label={home.recommended.label}
            hint={home.recommended.hint}
            aiFree={flows.get(home.recommended.flowId)?.usesModel === false}
            ai={flows.get(home.recommended.flowId)?.usesModel === true}
            onClick={() => home.recommended && start(home.recommended)}
          />
        </section>
      )}

      {generalFlows.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-[11px] font-medium tracking-wide text-text-500">よく使う</h3>
          <div className="grid grid-cols-2 gap-2">
            {generalFlows.map((flow, index) => (
              <ShortcutButton
                key={flow.flowId}
                label={flow.label}
                hint={flow.description}
                ai={flow.usesModel}
                wide={isLoneLast(index, generalFlows.length)}
                onClick={() => startFlow(flow)}
              />
            ))}
          </div>
        </section>
      )}

      <section className="space-y-2">
        <h3 className="text-[11px] font-medium tracking-wide text-text-500">
          {/* 上に何も無いとき（参加しているだけの人など）に「ほかに」は変なので言い換える */}
          {shortcuts.length > 0 || home.recommended || generalFlows.length > 0 ? 'ほかに' : 'できること'}
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {secondary.map((flow, index) => (
            <ShortcutButton
              key={flow.flowId}
              label={flow.label}
              hint={flow.usesModel ? flow.description : null}
              aiFree={!flow.usesModel}
              ai={flow.usesModel}
              wide={isLoneLast(index, secondary.length)}
              onClick={() => startFlow(flow)}
            />
          ))}
          <button
            type="button"
            onClick={onOpenAll}
            className="col-span-2 flex items-center justify-center gap-1 rounded-xl border border-text-200 px-3 py-2.5 text-sm text-text-700 hover:bg-text-50"
          >
            すべて見る
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </section>

      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {hasPrevious && (
          <button
            type="button"
            onClick={onOpenPrevious}
            className="flex items-center gap-1.5 text-xs text-text-500 underline-offset-2 hover:underline"
          >
            <ClockIcon className="h-4 w-4" />
            前の会話の続きを見る
          </button>
        )}
        <button
          type="button"
          onClick={onOpenHistory}
          className="flex items-center gap-1.5 text-xs text-text-500 underline-offset-2 hover:underline"
        >
          <ChatBubbleLeftRightIcon className="h-4 w-4" />
          これまでの会話
        </button>
      </div>

      <AiMarkLegend />
    </div>
  );
}
